import React, { useEffect, useState } from 'react';
import { logWarn } from '../lib/logger';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const STORAGE_KEY = 'ctrl-aero-cookie-consent';
export const COOKIE_SETTINGS_EVENT = 'ctrl-aero:open-cookie-settings';
export const COOKIE_CONSENT_CHANGED_EVENT = 'ctrl-aero:cookie-consent-changed';

/**
 * Dispatch this event anywhere in the app (e.g. from a footer link) to
 * re-open the cookie banner and let the user change their preferences.
 */
export const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const existing = localStorage.getItem(STORAGE_KEY);
        if (!existing) setVisible(true);
      } catch (e) {
        logWarn('cookie banner: storage unavailable', e);
        setVisible(true);
      }
    }, 500);

    const handler = () => setVisible(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, handler);
    return () => {
      clearTimeout(t);
      window.removeEventListener(COOKIE_SETTINGS_EVENT, handler);
    };
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, timestamp: new Date().toISOString() })
      );
    } catch (e) {
      logWarn('cookie banner: could not persist choice', e);
    }
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT));
    // Push consent state to Google Analytics (Consent Mode v2). Only
    // 'accepted-all' upgrades analytics + ads storage to 'granted'; every
    // other choice keeps them explicitly denied.
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        const granted = choice === 'accepted-all';
        window.gtag('consent', 'update', {
          ad_storage: granted ? 'granted' : 'denied',
          ad_user_data: granted ? 'granted' : 'denied',
          ad_personalization: granted ? 'granted' : 'denied',
          analytics_storage: granted ? 'granted' : 'denied',
        });
      }
    } catch (e) {
      logWarn('cookie banner: gtag consent update failed', e);
    }
    setVisible(false);
  };

  // Never surface the consent banner on the internal admin dashboard —
  // it's not a public marketing page and it obscures the analytics UI.
  if (location.pathname.startsWith('/admin')) return null;
  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto max-w-4xl bg-[#1e2d3b] text-white rounded-2xl shadow-2xl border border-white/10 pointer-events-auto max-h-[80vh] max-h-[80dvh] overflow-y-auto">
        <div className="p-4 sm:p-5">
          <p className="text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
            We use cookies to improve site performance, analyze traffic, and support marketing.
            You can choose which categories of cookies to allow. See our{' '}
            <Link to="/legal/cookies" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">
              Cookie Policy
            </Link>
            {' '}and{' '}
            <Link to="/legal/privacy" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">
              Privacy Notice
            </Link>
            .
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              type="button"
              onClick={() => persist('rejected')}
              variant="outline"
              data-testid="cookie-reject-all-btn"
              className="flex-1 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Decline All
            </Button>
            <Button
              type="button"
              onClick={() => persist('essential-only')}
              variant="outline"
              data-testid="cookie-essential-only-btn"
              className="flex-1 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Essential Only
            </Button>
            <Button
              type="button"
              onClick={() => persist('accepted-all')}
              data-testid="cookie-accept-all-btn"
              className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
            >
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
